import { useState, useEffect } from 'react';
import type { Envelope, EnvelopeCreate } from '@/types';
import { uploadMedia } from '@/services/api';

interface EnvelopeEditorProps {
    envelope: Envelope | null;
    onSave: (data: EnvelopeCreate) => Promise<void>;
    onCancel: () => void;
}

const EnvelopeEditor = ({ envelope, onSave, onCancel }: EnvelopeEditorProps) => {
    const [formData, setFormData] = useState<EnvelopeCreate>({
        title: envelope?.title || '',
        body: envelope?.body || '',
        style: envelope?.style || 'classic',
        envelope_design: envelope?.envelope_design || 'cream',
        is_published: envelope?.is_published || false,
        slug: envelope?.slug || '',
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (envelope) {
            setFormData({
                title: envelope.title,
                body: envelope.body,
                style: envelope.style,
                envelope_design: envelope.envelope_design || 'cream',
                is_published: envelope.is_published,
                slug: envelope.slug,
            });
        }
    }, [envelope]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSaving(true);

        try {
            await onSave(formData);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to save envelope');
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const media = await uploadMedia(file);
            // Insert image markdown at cursor position or end of body
            const imageMarkdown = `\n![${media.filename}](${media.url})\n`;
            setFormData({
                ...formData,
                body: formData.body + imageMarkdown,
            });
        } catch (err) {
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const insertMarkdown = (syntax: string) => {
        setFormData({
            ...formData,
            body: formData.body + syntax,
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-vintage-cream rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-vintage-paper px-6 py-4 border-b border-vintage-brown/20">
                    <h2 className="text-2xl font-serif font-bold text-vintage-ink">
                        {envelope ? 'Edit Envelope' : 'Create New Envelope'}
                    </h2>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                            Title *
                        </label>
                        <input
                            type="text"
                            required
                            className="input-field"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="A Letter from Paris"
                        />
                    </div>

                    {/* Slug */}
                    <div>
                        <label className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                            Slug (URL-friendly, auto-generated if empty)
                        </label>
                        <input
                            type="text"
                            className="input-field"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            placeholder="a-letter-from-paris"
                        />
                    </div>

                    {/* Style */}
                    <div>
                        <label className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                            Style Template
                        </label>
                        <select
                            className="input-field"
                            value={formData.style}
                            onChange={(e) =>
                                setFormData({ ...formData, style: e.target.value as any })
                            }
                        >
                            <option value="classic">Classic</option>
                            <option value="sepia">Sepia</option>
                            <option value="ink">Ink</option>
                        </select>
                    </div>

                    {/* Body Editor */}
                    <div>
                        <label className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                            Letter Body (Markdown) *
                        </label>

                        {/* Toolbar */}
                        <div className="flex flex-wrap gap-2 mb-2">
                            <button
                                type="button"
                                onClick={() => insertMarkdown('**bold text**')}
                                className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded"
                                title="Bold"
                            >
                                <strong>B</strong>
                            </button>
                            <button
                                type="button"
                                onClick={() => insertMarkdown('*italic text*')}
                                className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded"
                                title="Italic"
                            >
                                <em>I</em>
                            </button>
                        {/* Envelope Design */}
                        <div>
                            <label className="block text-sm font-serif font-semibold text-vintage-darkbrown mb-2">
                                Envelope Design
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {([
                                    { value: 'cream', label: 'Vintage Cream', color: '#F6F0E6' },
                                    { value: 'rose', label: 'Rose Blush', color: '#F5D5D8' },
                                    { value: 'sage', label: 'Sage Green', color: '#D4E5D4' },
                                    { value: 'lavender', label: 'Lavender', color: '#E6D9F5' },
                                    { value: 'peach', label: 'Peach', color: '#FFE5D0' },
                                    { value: 'mint', label: 'Mint', color: '#D5F0E8' },
                                    { value: 'burgundy', label: 'Burgundy', color: '#8B4049' },
                                ] as const).map((design) => (
                                    <button
                                        key={design.value}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, envelope_design: design.value as EnvelopeCreate['envelope_design'] })}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            formData.envelope_design === design.value
                                                ? 'border-vintage-brown shadow-lg scale-105'
                                                : 'border-vintage-brown/20 hover:border-vintage-brown/40'
                                        }`}
                                        style={{ backgroundColor: design.color }}
                                    >
                                        <div className="text-xs font-serif font-semibold text-vintage-ink">
                                            {design.label}
                                        </div>
                                        <div className="mt-1 text-center">
                                            {formData.envelope_design === design.value && '✓'}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                            <button
                                type="button"
                                onClick={() => insertMarkdown('\n## Heading\n')}
                                className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded"
                                title="Heading"
                            >
                                H
                            </button>
                            <button
                                type="button"
                                onClick={() => insertMarkdown('\n> Quote\n')}
                                className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded"
                                title="Quote"
                            >
                                "
                            </button>
                            <button
                                type="button"
                                onClick={() => insertMarkdown('\n- List item\n')}
                                className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded"
                                title="List"
                            >
                                •
                            </button>
                            <label className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded cursor-pointer">
                                {uploading ? 'Uploading...' : '📷 Image'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageUpload}
                                    disabled={uploading}
                                />
                            </label>
                            <button
                                type="button"
                                onClick={() =>
                                    insertMarkdown(
                                        '\n<iframe width="560" height="315" src="YOUTUBE_URL" frameborder="0" allowfullscreen></iframe>\n'
                                    )
                                }
                                className="px-3 py-1 text-sm bg-vintage-brown/10 hover:bg-vintage-brown/20 rounded"
                                title="Embed Video"
                            >
                                🎥 Video
                            </button>
                        </div>

                        <textarea
                            required
                            className="textarea-field min-h-[400px]"
                            value={formData.body}
                            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                            placeholder="Write your letter here using Markdown..."
                        />
                        <p className="text-xs text-vintage-brown mt-2">
                            Supports Markdown: **bold**, *italic*, # headers, links, images, embedded videos
                        </p>
                    </div>

                    {/* Published Toggle */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="published"
                            className="mr-2 w-4 h-4"
                            checked={formData.is_published}
                            onChange={(e) =>
                                setFormData({ ...formData, is_published: e.target.checked })
                            }
                        />
                        <label htmlFor="published" className="text-sm font-serif text-vintage-darkbrown">
                            Publish immediately (visible to public)
                        </label>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-4 pt-4 border-t border-vintage-brown/20">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn-secondary"
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary disabled:opacity-50"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : envelope ? 'Update Envelope' : 'Create Envelope'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnvelopeEditor;
